"use client";

import React, { useCallback, useContext, useRef } from "react";
import { AISummaryContext } from "./provider";
import type { AISummaryContextValue, SummaryRequest, UseSummaryOptions } from "./types";

export function useAISummary(): AISummaryContextValue {
  const context = useContext(AISummaryContext);
  if (!context) {
    throw new Error("useAISummary must be used within an AISummaryProvider");
  }
  return context;
}

export function useSummary(request: SummaryRequest, options?: UseSummaryOptions) {
  const context = useAISummary();
  const cacheKey = context.generateCacheKey(request);
  const { autoGenerate = false, onError } = options ?? {};
  const hasTriggeredAutoGenerate = useRef(false);

  React.useEffect(() => {
    if (!autoGenerate || hasTriggeredAutoGenerate.current) return;

    const summary = context.getSummary(cacheKey);
    const isLoading = context.isLoading(cacheKey);
    const hasError = context.hasError(cacheKey);
    if (summary || isLoading || hasError) return;

    hasTriggeredAutoGenerate.current = true;
    context.generateSummary(request).catch((err) => {
      const message = err instanceof Error ? err.message : "Failed to generate summary";
      onError?.(message);
    });
  }, [autoGenerate, cacheKey, request, context, onError]);

  // Returned handlers are consumed as dependencies (e.g. a `useCallback` around
  // `refresh`), so they must keep one identity until the context or request changes.
  const generate = useCallback(() => context.generateSummary(request), [context, request]);
  const refresh = useCallback(() => context.refreshSummary(request), [context, request]);
  const clear = useCallback(() => context.clearSummary(cacheKey), [context, cacheKey]);

  return {
    summary: context.getSummary(cacheKey),
    isLoading: context.isLoading(cacheKey),
    isStreaming: context.isStreaming(cacheKey),
    error: context.getError(cacheKey),
    hasError: context.hasError(cacheKey),
    generate,
    refresh,
    clear,
    cacheKey,
  };
}
